import { StateCreator } from "zustand"
import toast from "react-hot-toast"
import Swal from "sweetalert2"
import { AxiosError } from "axios"
import type * as StoreTypes from "@src/types/store"

import { getFromStorage, removeFromStorage, setToStorage, STORAGE_KEYS } from "@src/utils"
import { apiService } from "@src/services/apiService"
import { SwalConfig } from "@src/utils/SwalFire"
import { useAuthStore } from "@store/authStore"

import PersonalityTraitsSource from "@pages/LegacyProfiles/data/personalities_and_traits.json"

const initState = () => ({
  personalityTraits: PersonalityTraitsSource,
  legacyProfiles: [],
  legacyProfilesPagination: null,
  currentProfile: null,
})

export const createLegacyProfilesSlice: StateCreator<
  StoreTypes.StoreState,
  [],
  [],
  StoreTypes.LegacyProfilesSlice
> = (set, get) => ({
  ...{ ...initState(), currentProfile: getFromStorage(STORAGE_KEYS.CURRENT_PROFILE, null) },
  getProfiles: async (params = {}, config = {}) => {
    const { force = false } = config
    const { legacyProfiles, loading } = get()
    const isAdmin = useAuthStore.getState().isAdmin()
    if (loading === "FETCHING_LEGACY_PROFILES") return
    if ((!force && legacyProfiles.length > 0) || isAdmin) return
    try {
      set({ loading: "FETCHING_LEGACY_PROFILES" })
      const response = await apiService.legacy.getProfiles(params)
      const { data, pagination } = response.data
      let newCurrentProfile = get().currentProfile
      if (!get().currentProfile && data.length !== 0) {
        newCurrentProfile = data[0]
      }
      set({ legacyProfiles: data, legacyProfilesPagination: pagination, currentProfile: newCurrentProfile })
    } catch (error) {
      set({ legacyProfiles: [], legacyProfilesPagination: null, currentProfile: null })
      removeFromStorage(STORAGE_KEYS.CURRENT_PROFILE)
    } finally {
      set({ loading: null })
    }
  },
  setCurrentProfile: async (newProfile: LegacyProfile | null) => {
    if (!newProfile || Object.keys(newProfile).length === 0) {
      set({ currentProfile: null })
      removeFromStorage(STORAGE_KEYS.CURRENT_PROFILE)
      return
    }
    try {
      const hasSession = get().legacyProfiles.find((lp) => lp.id === newProfile.id && !!lp.open_session)
      if (hasSession) {
        set({ currentProfile: newProfile })
        setToStorage(STORAGE_KEYS.CURRENT_PROFILE, newProfile)
        return
      }
      set({ loading: "CREATING_SESSION" })
      const response = await apiService.sessions.start({ assistant_id: newProfile?.id })
      const { status, data: open_session } = response.data as BaseResponse<IOpenSession>
      if (!status) {
        throw response.data
      }
      set((state) => ({
        legacyProfiles: state.legacyProfiles.map((lp) =>
          lp.id == newProfile.id ? { ...lp, open_session } : lp,
        ),
        currentProfile: { ...newProfile, open_session },
        listMediasPagination: null,
        listMedias: [],
      }))
      setToStorage(STORAGE_KEYS.CURRENT_PROFILE, { ...newProfile, open_session })
      toast.success("Session started successfully")
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        text: "Error starting session. Please try again.",
        confirmButtonText: "Continue",
      })
      removeFromStorage(STORAGE_KEYS.CURRENT_PROFILE)
    } finally {
      set({ loading: null })
    }
  },
  addProfile: async (formData: any) => {
    try {
      const response = await apiService.legacy.createProfile(formData)
      const { status, data } = response.data as BaseResponse<LegacyProfile>
      if (!status) {
        throw response.data
      }
      if (formData.avatar) {
        const responseAvatar = await apiService.legacy.updateAvatar({
          assistant_id: data.id,
          file: formData.avatar,
        })
        const { status, data: avatarData } = responseAvatar.data as BaseResponse<LegacyProfile>
        if (!status) {
          throw responseAvatar.data
        }
        data.avatar_path = avatarData.avatar_path
      }
      set((state) => ({
        legacyProfiles: [data, ...state.legacyProfiles],
      }))
    } catch (error: any) {
      const _error_ = error.response?.data
      if (_error_.status >= 400) {
        if ("errors" in _error_.data) {
          throw _error_.data || ({} as ValidationError)
        }
        throw _error_.data || ({} as GenericError)
      }
      throw error
    }
  },
  updateProfile: async (id: string, updates: any) => {
    try {
      if (updates.avatar) {
        const response = await apiService.legacy.updateAvatar({
          assistant_id: id,
          file: updates.avatar,
        })
        const { status } = response.data as BaseResponse<LegacyProfile>
        if (!status) {
          throw response.data
        }
      }
      const response = await apiService.legacy.updateProfile(id, updates)
      const { status, data } = response.data as BaseResponse<LegacyProfile>
      if (!status) {
        throw response.data
      }
      set((state) => ({
        legacyProfiles: state.legacyProfiles.map((profile) =>
          profile.id === id ? { ...profile, ...data } : profile,
        ),
        currentProfile:
          state.currentProfile?.id === id ? { ...state.currentProfile, ...data } : state.currentProfile,
      }))
    } catch (error: any) {
      const _error_ = error.response?.data
      if (_error_.status >= 400) {
        if ("errors" in _error_.data) {
          throw _error_.data || ({} as ValidationError)
        }
        throw _error_.data || ({} as GenericError)
      }
      throw error
    }
  },
  deleteProfile: async (uuid: string) => {
    const preConfirmDelete = async () => {
      try {
        Swal.showLoading()
        const response = await apiService.legacy.deleteProfile(uuid)
        const { status, msg } = response.data
        if (!status) {
          Swal.fire({
            icon: "error",
            title: "Something went wrong",
            text: (msg as string) || "Error deleting profile",
            confirmButtonText: "Continue",
          })
          throw new Error(msg as string)
        }
        return new Promise((resolve) => resolve(true))
      } catch (error: any) {
        console.error("preConfirmDelete:error", error)
        if (error instanceof AxiosError) {
          if (error.response?.data) {
            Swal.fire({
              icon: "error",
              title: "Something went wrong",
              text: error.response.data?.errors?.assistant[0] || "Error deleting profile",
              confirmButtonText: "Continue",
            })
            return new Promise((resolve) => resolve(false))
          }
        }
        throw error
      }
    }
    try {
      await Swal.fire(SwalConfig.confirmDestroy({ preConfirm: preConfirmDelete.bind(null) }))
      set((state) => ({
        legacyProfiles: state.legacyProfiles.filter((profile) => profile.id != uuid),
        currentProfile: state.currentProfile?.id == uuid ? null : state.currentProfile,
      }))
      toast.success("Profile deleted successfully")
    } catch (error) {
      // console.error("onDelete:error", error)
      toast.error("Error deleting profile. Please try again.")
    }
  },
  resetLegacyProfiles: () => {
    set({ ...initState() })
  },
})
